/*
 * assessmentsContentData — researched client-side content registry
 * for the most common public-facing self-assessments on AUMHUM.
 *
 * The backend's AssessmentTest document owns the structural data:
 * title, description, cover image, questions, scoring, domains,
 * FAQs. This registry adds marketing-and-clinical display content
 * that isn't yet in the DB — a plain-language overview, score
 * bands, a sample item, when-to-take / when-not-enough guidance,
 * the typical estimated time and item count, and the condition the
 * assessment screens for (so we can cross-link to the condition
 * page).
 *
 * Rationale for keeping this client-side (for now):
 *   - It lets us ship researched copy without a DB migration.
 *   - The content is pure presentation — if we later move it into
 *     the AssessmentTest model, callers only need a single switch.
 *
 * Tone across every entry matches the rest of the public site:
 * empathetic, clinical, neutral between therapy & medication, no
 * dramatization.
 */

// ── helpers ──────────────────────────────────────────────────────

export const SCORE_TONES = {
  ok: '#0f766e',       // teal-700
  mild: '#0369a1',     // sky-700
  mod: '#b45309',      // amber-700
  sig: '#c2410c',      // orange-700
  urgent: '#be123c',   // rose-700
}

/**
 * Look up assessment-specific content by slug. Returns null if the
 * slug isn't in the registry — callers fall back to server data.
 */
export function getAssessmentContent(slug) {
  if (!slug) return null
  return ASSESSMENTS[String(slug).trim().toLowerCase()] || null
}

// ── registry ─────────────────────────────────────────────────────

export const ASSESSMENTS = {
  'phq-9': {
    shortName: 'PHQ-9',
    fullName: 'Patient Health Questionnaire — 9 items',
    category: 'Depression',
    relatedConditionSlug: 'depression',
    accent: 'indigo',
    timeMin: 3,
    items: 9,
    measures: 'Depression severity over the past two weeks',
    validatedBy:
      'Developed by Spitzer, Kroenke and colleagues (1999). Validated across primary-care and psychiatric populations worldwide; the standard depression screener in most clinical settings.',
    overview: [
      'The PHQ-9 is the depression screener most widely used in clinical practice. Each of its nine questions maps to one of the DSM-5 criteria for depression, and the total gives clinicians a quick, reliable read on severity.',
      'You\u2019ll answer 9 short questions about how you\u2019ve felt over the last two weeks. It takes about three minutes.',
    ],
    scoreBands: [
      { range: '0–4', label: 'Minimal or no depression', tone: 'ok' },
      { range: '5–9', label: 'Mild', tone: 'mild' },
      { range: '10–14', label: 'Moderate', tone: 'mod' },
      { range: '15–19', label: 'Moderately severe', tone: 'sig' },
      { range: '20–27', label: 'Severe', tone: 'urgent' },
    ],
    sampleItem:
      '\u201cOver the last 2 weeks, how often have you been bothered by little interest or pleasure in doing things?\u201d',
    whenToTake: [
      'Low or flat mood has stretched past a couple of weeks',
      'Sleep, appetite, or energy have clearly shifted',
      'Someone who knows you well has raised it with you',
    ],
    whenNotEnough:
      'If your score falls in the moderately-severe or severe range \u2014 or if you\u2019re having thoughts of self-harm \u2014 please skip the wait and talk to our care team the same day.',
    encouragement:
      'Three minutes now often saves weeks of second-guessing. Either way you\u2019ll leave with a clearer picture.',
  },

  'gad-7': {
    shortName: 'GAD-7',
    fullName: 'Generalised Anxiety Disorder — 7 items',
    category: 'Anxiety',
    relatedConditionSlug: 'anxiety',
    accent: 'sky',
    timeMin: 2,
    items: 7,
    measures: 'Anxiety severity over the past two weeks',
    validatedBy:
      'Developed by Spitzer, Kroenke, Williams & L\u00f6we (2006). Validated as a primary-care anxiety screener; widely used across outpatient psychiatry and research.',
    overview: [
      'The GAD-7 is the most commonly used anxiety screener in general clinical practice. Seven quick items capture the cluster of symptoms that define generalised anxiety, and it correlates well with panic, social anxiety, and PTSD too.',
      'The whole thing takes about two minutes.',
    ],
    scoreBands: [
      { range: '0–4', label: 'Minimal anxiety', tone: 'ok' },
      { range: '5–9', label: 'Mild', tone: 'mild' },
      { range: '10–14', label: 'Moderate', tone: 'mod' },
      { range: '15–21', label: 'Severe', tone: 'urgent' },
    ],
    sampleItem:
      '\u201cOver the last 2 weeks, how often have you been bothered by feeling nervous, anxious, or on edge?\u201d',
    whenToTake: [
      'You\u2019ve been noticing worry that doesn\u2019t ease with evidence',
      'Sleep is being disrupted more than a couple of nights a week',
      'You\u2019re avoiding things you\u2019d otherwise want to do',
    ],
    whenNotEnough:
      'A score of 10+ usually means it\u2019s worth a conversation with a clinician \u2014 especially if the pattern has been around for a while.',
    encouragement:
      'Anxiety tends to shrink when it\u2019s named accurately. Two minutes of answering helps with that.',
  },

  'beck-depression-inventory': {
    shortName: 'BDI-II',
    fullName: 'Beck Depression Inventory — II',
    category: 'Depression',
    relatedConditionSlug: 'depression',
    accent: 'indigo',
    timeMin: 8,
    items: 21,
    measures: 'Depression severity across mood, cognition, and somatic symptoms',
    validatedBy:
      'Developed by Aaron T. Beck (1996 revision). One of the most-cited self-report depression measures; used in both research and clinical practice for decades.',
    overview: [
      'The Beck Depression Inventory II is a longer, more granular alternative to the PHQ-9. Its 21 items cover affective, cognitive, and somatic symptoms \u2014 giving a richer picture when nuance matters.',
      'Most people finish in under ten minutes.',
    ],
    scoreBands: [
      { range: '0–13', label: 'Minimal', tone: 'ok' },
      { range: '14–19', label: 'Mild', tone: 'mild' },
      { range: '20–28', label: 'Moderate', tone: 'mod' },
      { range: '29–63', label: 'Severe', tone: 'urgent' },
    ],
    sampleItem:
      '\u201cPick the statement that best describes how you\u2019ve been feeling about the future over the past two weeks.\u201d',
    whenToTake: [
      'You\u2019d like a more detailed picture than a shorter screener gives',
      'You\u2019ve already taken the PHQ-9 and want corroboration',
      'You want to track change over time at regular clinical visits',
    ],
    whenNotEnough:
      'The BDI is a measurement tool, not a diagnosis. Any score in the moderate-or-above band deserves a conversation with a clinician.',
    encouragement:
      'A richer map makes a clearer plan. Take your time with this one.',
  },

  'y-bocs': {
    shortName: 'Y-BOCS (Self-Report)',
    fullName: 'Yale-Brown Obsessive Compulsive Scale — self-report',
    category: 'OCD',
    relatedConditionSlug: 'ocd',
    accent: 'violet',
    timeMin: 10,
    items: 10,
    measures: 'OCD severity across obsessions and compulsions',
    validatedBy:
      'Developed by Goodman, Price and colleagues (1989) \u2014 the gold-standard OCD severity measure. The self-report version is widely used for screening and tracking.',
    overview: [
      'The Y-BOCS is the most important measure in OCD. It separates obsessions and compulsions into two scales and captures time, interference, distress, resistance, and control for each.',
      'This self-report version takes about ten minutes and gives you a clear picture of where things stand.',
    ],
    scoreBands: [
      { range: '0–7', label: 'Subclinical', tone: 'ok' },
      { range: '8–15', label: 'Mild', tone: 'mild' },
      { range: '16–23', label: 'Moderate', tone: 'mod' },
      { range: '24–31', label: 'Severe', tone: 'sig' },
      { range: '32–40', label: 'Extreme', tone: 'urgent' },
    ],
    sampleItem:
      '\u201cHow much time do you spend thinking about the obsessions on a typical day?\u201d',
    whenToTake: [
      'Intrusive thoughts or rituals have started taking meaningful time',
      'You\u2019ve been avoiding triggers to keep the obsessions quiet',
      'Family members have noticed or been drawn into reassurance',
    ],
    whenNotEnough:
      'Y-BOCS scores of 16+ typically mean specialist OCD care (ERP therapy, often with medication) will make a real difference. Talk to our care team.',
    encouragement:
      'OCD responds particularly well to the right kind of help. Naming the severity is the first concrete step.',
  },

  'oci-r': {
    shortName: 'OCI-R',
    fullName: 'Obsessive-Compulsive Inventory — Revised',
    category: 'OCD',
    relatedConditionSlug: 'ocd',
    accent: 'violet',
    timeMin: 4,
    items: 18,
    measures: 'Breadth of OCD symptoms across six domains',
    validatedBy:
      'Developed by Foa, Huppert and colleagues (2002). Widely used as a brief dimensional screener that surfaces OCD subtypes (washing, checking, ordering, obsessing, hoarding, neutralising).',
    overview: [
      'The OCI-R complements the Y-BOCS. Where Y-BOCS measures severity, OCI-R maps the shape \u2014 which specific clusters of OCD symptoms are prominent for you right now.',
      'It\u2019s short (18 items, 3\u20135 minutes) and particularly useful as a first-pass screen.',
    ],
    scoreBands: [
      { range: '0–20', label: 'Typical range', tone: 'ok' },
      { range: '21–27', label: 'Elevated', tone: 'mild' },
      { range: '28–40', label: 'Likely clinical OCD', tone: 'mod' },
      { range: '41+',   label: 'Strong indication',    tone: 'urgent' },
    ],
    sampleItem:
      '\u201cHow much does the thought of doing something silly or embarrassing in public distress you?\u201d',
    whenToTake: [
      'You\u2019re not sure if what you\u2019re experiencing is OCD',
      'You want a broader map before taking the fuller Y-BOCS',
      'Clinician asked you to complete a screener before your visit',
    ],
    whenNotEnough:
      'A screen is a starting point. For an OCD-specific plan, an ERP-trained clinician is the next step.',
    encouragement:
      'OCD hides in plain sight \u2014 sometimes for years. A short screen can finally name it.',
  },

  'mdq': {
    shortName: 'MDQ',
    fullName: 'Mood Disorder Questionnaire',
    category: 'Bipolar Disorder',
    relatedConditionSlug: 'bipolar-disorder',
    accent: 'indigo',
    timeMin: 3,
    items: 15,
    measures: 'Screen for bipolar-spectrum symptoms',
    validatedBy:
      'Developed by Hirschfeld and colleagues (2000). The most-used bipolar-spectrum screener \u2014 designed to catch the manic/hypomanic history that depression alone misses.',
    overview: [
      'Depression and bipolar depression look the same from the outside. The MDQ asks about lifetime episodes of elevated mood, energy, and reduced need for sleep \u2014 the history that changes the treatment plan.',
      'It\u2019s quick: 15 yes/no items plus a co-occurrence question. About three minutes.',
    ],
    scoreBands: [
      { range: 'Low', label: 'Bipolar-spectrum symptoms unlikely', tone: 'ok' },
      { range: 'Positive', label: 'Further psychiatric evaluation recommended', tone: 'mod' },
    ],
    sampleItem:
      '\u201cHas there ever been a period of time when you were not your usual self, and you felt so good or so hyper that other people thought you were not your normal self?\u201d',
    whenToTake: [
      'You\u2019ve been treated for depression but something doesn\u2019t fit',
      'There\u2019s a family history of bipolar disorder',
      'Someone close to you has noticed unusual high-energy periods',
    ],
    whenNotEnough:
      'MDQ is a screener, not a diagnosis. A positive result means a psychiatric consult is the clear next step \u2014 the right treatment is distinct from depression alone.',
    encouragement:
      'Getting this right changes everything downstream. Three minutes of honest answers matters.',
  },

  'audit': {
    shortName: 'AUDIT',
    fullName: 'Alcohol Use Disorders Identification Test',
    category: 'Alcoholism',
    relatedConditionSlug: 'alcoholism',
    accent: 'indigo',
    timeMin: 3,
    items: 10,
    measures: 'Hazardous drinking and alcohol dependence risk',
    validatedBy:
      'Developed by the World Health Organization (1989). The most widely validated alcohol screener, used globally across primary care, psychiatry, and public-health programmes.',
    overview: [
      'AUDIT maps drinking patterns across three zones: how much and how often you drink, dependence symptoms, and alcohol-related harm. It\u2019s not about judgement \u2014 it\u2019s about a clear starting point.',
      'Ten items, about three minutes.',
    ],
    scoreBands: [
      { range: '0–7',   label: 'Lower-risk drinking', tone: 'ok' },
      { range: '8–15',  label: 'Increasing risk',     tone: 'mild' },
      { range: '16–19', label: 'Higher risk',         tone: 'mod' },
      { range: '20+',   label: 'Possible dependence', tone: 'urgent' },
    ],
    sampleItem:
      '\u201cHow often during the last year have you found that you were not able to stop drinking once you had started?\u201d',
    whenToTake: [
      'Drinking patterns have started to concern you (or someone close to you)',
      'You\u2019ve tried to cut down and it hasn\u2019t stuck',
      'You\u2019d like an honest baseline before any plan',
    ],
    whenNotEnough:
      'A score of 16+ suggests professional support is the right next step. For anyone physically dependent, medically-supervised withdrawal is the safest path \u2014 please talk to our team before stopping abruptly.',
    encouragement:
      'Change starts with a clear picture, not a judgement. This is that picture.',
  },

  'dast-10': {
    shortName: 'DAST-10',
    fullName: 'Drug Abuse Screening Test — 10 items',
    category: 'Drug use',
    relatedConditionSlug: 'drug-addiction',
    accent: 'violet',
    timeMin: 3,
    items: 10,
    measures: 'Substance-use-disorder risk (non-alcohol substances)',
    validatedBy:
      'Developed by Skinner (1982). Widely used across clinical and research settings as a brief substance-use screener.',
    overview: [
      'DAST-10 asks about non-medical drug use patterns and their consequences over the past year. It covers prescription misuse as well as illicit substances.',
      'Ten yes/no items, about three minutes.',
    ],
    scoreBands: [
      { range: '0',    label: 'No reported problems',    tone: 'ok' },
      { range: '1–2',  label: 'Low level',                tone: 'mild' },
      { range: '3–5',  label: 'Moderate level',           tone: 'mod' },
      { range: '6–8',  label: 'Substantial level',        tone: 'sig' },
      { range: '9–10', label: 'Severe level',             tone: 'urgent' },
    ],
    sampleItem:
      '\u201cIn the past 12 months, have you had medical problems as a result of your drug use?\u201d',
    whenToTake: [
      'Use has outpaced what you intended over the past year',
      'A prescription medication has become hard to control',
      'Friends or family have gently raised it with you',
    ],
    whenNotEnough:
      'Scores of 3+ benefit from a clinical conversation. For opioid or stimulant use specifically, the care team can talk you through medication options that materially improve outcomes.',
    encouragement:
      'Screeners are honest without being harsh. It\u2019s a useful baseline, not a verdict.',
  },

  'assist': {
    shortName: 'ASSIST',
    fullName: 'Alcohol, Smoking and Substance Involvement Screening Test',
    category: 'Substance use',
    relatedConditionSlug: 'addiction',
    accent: 'violet',
    timeMin: 8,
    items: 8,
    measures: 'Substance-specific risk across alcohol, tobacco, and illicit drugs',
    validatedBy:
      'Developed by the World Health Organization working group. Validated across primary care globally; covers 10 substances with a common question structure.',
    overview: [
      'ASSIST is broader than AUDIT or DAST \u2014 it screens across alcohol, tobacco, cannabis, cocaine, stimulants, inhalants, sedatives, hallucinogens, opioids and \u201cother\u201d. Each substance gets its own risk score.',
      'Most people finish in 5\u20138 minutes.',
    ],
    scoreBands: [
      { range: 'Low',      label: 'Brief advice',               tone: 'ok' },
      { range: 'Moderate', label: 'Brief intervention + monitor', tone: 'mod' },
      { range: 'High',     label: 'Intensive treatment',          tone: 'urgent' },
    ],
    sampleItem:
      '\u201cDuring the past three months, how often have you had a strong desire or urge to use [substance]?\u201d',
    whenToTake: [
      'Multiple substances are involved',
      'You want a single overview across everything you use',
      'You\u2019re headed into an assessment and want a baseline first',
    ],
    whenNotEnough:
      'A high-risk score on any substance should be reviewed with a clinician \u2014 especially for opioids, benzodiazepines, or alcohol where withdrawal itself needs medical care.',
    encouragement:
      'One honest pass covers everything at once. It\u2019s worth the time.',
  },

  'aq-10': {
    shortName: 'AQ-10',
    fullName: 'Autism Spectrum Quotient — 10 items',
    category: 'Autism Spectrum',
    relatedConditionSlug: 'autism-spectrum-disorder',
    accent: 'sky',
    timeMin: 3,
    items: 10,
    measures: 'Adult autism-spectrum screening',
    validatedBy:
      'Developed by Simon Baron-Cohen and colleagues at the University of Cambridge. The AQ-10 is the short version recommended by NICE for adult autism screening in primary care.',
    overview: [
      'AQ-10 is a short screener that helps surface whether a formal autism assessment might be worth pursuing. It\u2019s not diagnostic \u2014 it\u2019s a filter.',
      'Ten statements, about three minutes.',
    ],
    scoreBands: [
      { range: '0–5', label: 'Below screening threshold', tone: 'ok' },
      { range: '6–10', label: 'Further assessment recommended', tone: 'mod' },
    ],
    sampleItem:
      '\u201cI often notice small sounds when others do not.\u201d (Definitely agree \u2014 Slightly agree \u2014 Slightly disagree \u2014 Definitely disagree)',
    whenToTake: [
      'You\u2019ve always felt a little out of sync socially and wondered why',
      'Someone has suggested you look into it',
      'Your child (or adolescent) has been showing patterns that persist',
    ],
    whenNotEnough:
      'A score of 6+ on AQ-10 is a prompt to pursue a full developmental assessment \u2014 not a diagnosis. A specialist clinician does the clinical work from there.',
    encouragement:
      'Whatever the score, a clearer picture of yourself is a good outcome. The assessment is short.',
  },

  'rbq-2a': {
    shortName: 'RBQ-2A',
    fullName: 'Repetitive Behaviour Questionnaire — Adult',
    category: 'Autism Spectrum',
    relatedConditionSlug: 'autism-spectrum-disorder',
    accent: 'sky',
    timeMin: 6,
    items: 20,
    measures: 'Restricted & repetitive behaviour patterns in adults',
    validatedBy:
      'Developed by Barrett and colleagues (2015); used alongside screeners like AQ-10 to characterise restricted/repetitive behaviour.',
    overview: [
      'RBQ-2A focuses specifically on the repetitive-behaviour and routine dimensions of the autism spectrum \u2014 an area AQ-10 summarises only briefly. It helps refine the picture before or alongside a formal assessment.',
      '20 items, about six minutes.',
    ],
    scoreBands: [
      { range: '20–39', label: 'Typical range',         tone: 'ok' },
      { range: '40–59', label: 'Elevated',              tone: 'mild' },
      { range: '60+',   label: 'Further work warranted', tone: 'mod' },
    ],
    sampleItem:
      '\u201cI have a strong desire to stick to routines and get upset if they change.\u201d (Never \u2014 Rarely \u2014 Sometimes \u2014 Often)',
    whenToTake: [
      'Routines and sensory sensitivities have always been a bigger part of life than others seem to notice',
      'You\u2019ve already taken AQ-10 and want more detail',
    ],
    whenNotEnough:
      'As with AQ-10, this is a screener. Diagnosis \u2014 and the supports that come with it \u2014 requires specialist assessment.',
    encouragement:
      'Precise maps help. Specific self-knowledge opens specific support.',
  },

  'pdq-4': {
    shortName: 'PDQ-4+',
    fullName: 'Personality Diagnostic Questionnaire — 4+',
    category: 'Personality',
    relatedConditionSlug: 'personality-disorder',
    accent: 'violet',
    timeMin: 15,
    items: 99,
    measures: 'Screen across all DSM-IV personality-disorder categories',
    validatedBy:
      'Developed by Steven Hyler (1994). A widely-used self-report screening instrument for personality-disorder traits; screens for all ten DSM-IV personality disorders plus appendix categories.',
    overview: [
      'PDQ-4+ is a comprehensive personality-disorder screener. A positive result on any subscale is an indication that the trait pattern is worth exploring with a clinician \u2014 it isn\u2019t a diagnosis on its own.',
      'It\u2019s long (99 true/false items, 15\u201320 minutes) on purpose \u2014 breadth matters here.',
    ],
    scoreBands: [
      { range: 'Per subscale', label: 'Threshold varies by PD category \u2014 clinician interprets', tone: 'mod' },
    ],
    sampleItem:
      '\u201cI have given up doing some things because I was afraid I would not do them well enough.\u201d (True / False)',
    whenToTake: [
      'Relationship patterns keep repeating in ways that concern you',
      'A clinician has raised the possibility of a personality-disorder diagnosis',
      'You want breadth rather than a single-trait screen',
    ],
    whenNotEnough:
      'PDQ-4+ is a starting map. Real personality-disorder work takes structured, long-term therapy \u2014 and the right therapist fit matters more than the specific label.',
    encouragement:
      'This is a longer screen because your experience is more than a label. Take your time.',
  },

  'msi-bpd': {
    shortName: 'MSI-BPD',
    fullName: 'McLean Screening Instrument for Borderline Personality Disorder',
    category: 'Personality',
    relatedConditionSlug: 'personality-disorder',
    accent: 'violet',
    timeMin: 3,
    items: 10,
    measures: 'Screen for borderline personality disorder',
    validatedBy:
      'Developed by Zanarini and colleagues at McLean Hospital (2003). A brief, validated screener focused specifically on BPD.',
    overview: [
      'MSI-BPD is a focused 10-item screener for borderline personality disorder. Where PDQ-4+ casts wide, MSI-BPD is narrow and quick.',
      'Ten yes/no items, about three minutes.',
    ],
    scoreBands: [
      { range: '0–6', label: 'Below screening threshold', tone: 'ok' },
      { range: '7–10', label: 'Further BPD assessment recommended', tone: 'mod' },
    ],
    sampleItem:
      '\u201cHave you been extremely moody? (Short periods of intense sadness, irritability, or anxiety lasting hours.)\u201d',
    whenToTake: [
      'Intense emotions and relationship difficulties have been a recurring theme',
      'A clinician has raised the possibility and you want a brief self-check',
    ],
    whenNotEnough:
      'BPD is specifically-treatable with structured therapy (DBT, MBT, Schema). A positive screen is an invitation to start that conversation, not an answer.',
    encouragement:
      'BPD has one of the best treatment-response stories in modern psychiatry. Naming it early matters.',
  },

  'panic-disorder-severity': {
    shortName: 'PDSS-SR',
    fullName: 'Panic Disorder Severity Scale — Self-Report',
    category: 'Anxiety',
    relatedConditionSlug: 'anxiety',
    accent: 'sky',
    timeMin: 4,
    items: 7,
    measures: 'Panic disorder severity across frequency, distress, avoidance, and impairment',
    validatedBy:
      'Developed by Shear and colleagues (1997); the self-report version is used to track panic-disorder severity longitudinally.',
    overview: [
      'If panic attacks are a part of the picture, the GAD-7 tends to under-measure them. PDSS-SR is the specific tool \u2014 it maps panic frequency, anticipatory anxiety, avoidance, and the impact on work and relationships.',
      'Seven items, under five minutes.',
    ],
    scoreBands: [
      { range: '0–3', label: 'Minimal',      tone: 'ok' },
      { range: '4–7', label: 'Mild',          tone: 'mild' },
      { range: '8–11', label: 'Moderate',      tone: 'mod' },
      { range: '12–15', label: 'Severe',       tone: 'sig' },
      { range: '16+', label: 'Extreme',       tone: 'urgent' },
    ],
    sampleItem:
      '\u201cHow much did you worry or feel anxious about when your next panic attack would occur?\u201d',
    whenToTake: [
      'You\u2019ve had more than one panic attack',
      'You\u2019ve started avoiding places or situations to prevent them',
      'You\u2019re already in treatment and want to track change',
    ],
    whenNotEnough:
      'Panic responds well to specific therapy (CBT, interoceptive exposure). If the score is moderate or above, that\u2019s the conversation worth having soon.',
    encouragement:
      'Panic feels enormous while it\u2019s happening and shrinks fast with the right help.',
  },
}
